// Picking repos at scale (#7802, owner 2026-11-02: "think an org with 1000s of repos"): the
// vocabulary a person's bulk enable speaks on all three surfaces (REST, MCP, the dashboard),
// and the verdict that gates one repo inside it.
//
// PURE, and here rather than in `apps/api` because the dashboard reads the same filter and
// recipe vocabulary and apps never import apps. The act itself is `apps/api`
// `deps/repo-enablement.ts`; what it queues is drained by `apps/worker`
// `cron/repo-setup-drain.ts`.

import { z } from 'zod';
import { type Forge, GITHUB_FORGE, GITLAB_FORGE } from './forge';

/**
 * WHERE AND HOW: what a person picks with the repos (owner: "he says where he wants and how,
 * because we provide recipes"). A MAINTENANCE RECIPE is a named starting policy, nothing
 * else: the setup pass renders it into `.dz/maintainer/maintainer.yml`, the repo's own file,
 * proposed in the onboarding pull request a person merges. No second config lives anywhere:
 * after that merge the committed file is the whole truth, and editing it is how a repo
 * leaves its recipe. A repo that already carries a policy file keeps it.
 *
 * Unrelated to the MCP `prompts/*` recipes (`packages/mcp/src/recipes.ts`), which are prose
 * journeys naming verbs; `enable_repos` is the one that walks this vocabulary.
 *
 * - `maintain_auto_merge`: the house posture the pass already proposes. Issues are worked,
 *   pull requests reviewed, and merged once the machine gates pass.
 * - `review_only`: reviews pull requests, opens none, works no issue, never merges.
 * - `issues_only`: works issues into pull requests a person merges, and reviews nobody's.
 */
export const MaintenanceRecipe = {
  MaintainAutoMerge: 'maintain_auto_merge',
  ReviewOnly: 'review_only',
  IssuesOnly: 'issues_only',
} as const;
export const MaintenanceRecipeSchema = z.enum(MaintenanceRecipe);
export type MaintenanceRecipe = z.infer<typeof MaintenanceRecipeSchema>;

/**
 * The token a pass names (`requires`) when only a recent box can render its recipe: the
 * platform derives it from the reported build (`RunnerCapability.SetupRecipes`, its one other
 * spelling, held equal by `repo-enablement.test.ts`). A build older than the field strips it and proposes the house
 * posture, `auto_merge: true`, for a person who chose `review_only`, so a recipe other than the
 * house posture waits for a box that knows it. The house recipe needs no token: every build
 * proposes exactly that document.
 */
export const SETUP_RECIPE_CAPABILITY = 'harness:setup-recipes';

/** Every recipe, in the order a person is offered them. */
export const MAINTENANCE_RECIPES: readonly MaintenanceRecipe[] =
  MaintenanceRecipeSchema.options.slice();

/** Longest `active_within_days` a filter takes: GitHub's `pushed_at` is the fact, not a metric. */
export const REPO_ACTIVITY_MAX_DAYS = 3650;

/**
 * WHICH repos: the list's filters and "select all matching" are ONE shape, so what a person
 * previews is exactly what the act walks. Absent means no narrowing on that field.
 */
export const RepoEnablementFilterSchema = z
  .object({
    /** Case-insensitive substring of `owner/name`. Never a pattern: `%` matches a literal `%`. */
    q: z.string().trim().min(1).max(200).optional(),
    language: z.string().min(1).max(64).optional(),
    topic: z.string().min(1).max(64).optional(),
    visibility: z.enum(['public', 'private']).optional(),
    enabled: z.boolean().optional(),
    /** Pushed to within this many days. A row whose push is unknown never matches. */
    active_within_days: z.number().int().min(1).max(REPO_ACTIVITY_MAX_DAYS).optional(),
  })
  .strict();
export type RepoEnablementFilter = z.infer<typeof RepoEnablementFilterSchema>;

export const RepoBulkAction = { Enable: 'enable', Disable: 'disable' } as const;
export const RepoBulkActionSchema = z.enum(RepoBulkAction);
export type RepoBulkAction = z.infer<typeof RepoBulkActionSchema>;

/**
 * THE PACE, and the arithmetic that sets it.
 *
 * Measured before: an install enabled 324 repos in 8 seconds and minted 324 setup tasks
 * (#7802). One released repo costs GitHub a policy read (`policy.ingest`, up to 3 calls) and
 * a backlog walk (a page per 100 open issues), all on that account's installation token, whose
 * primary budget is 5,000 calls an hour and whose secondary budget is about 900 points a
 * minute. Six repos a minute is 360 an hour: it stays inside the primary budget up to 13
 * calls a repo and leaves the installation's webhook-driven work the rest. An account's rate
 * is an upper bound for each of its installations, because an installation has one account.
 *
 * 5,000 repos at this pace take about 14 hours. That is the point: nothing is dropped, it is
 * a queue, and the answer states the wait.
 */
export const REPO_SETUP_RELEASE_PER_MINUTE = 6;

/**
 * The share of the account's own box slots setup passes may hold: a setup pass is
 * `TaskClass.Interactive`, which claims ahead of every coding, CI and gardener task
 * (`TASK_CLASS_SCHEDULE_ORDER`), so a queue free to fill the account's slots would starve
 * that account's other work for the 14 hours above. Half leaves the rest to everything else.
 */
export const REPO_SETUP_MAX_SLOT_SHARE = 0.5;

/**
 * How many queued setups one account may release this tick: never past the per-minute rate,
 * and never past its live-setup window. The window is at least 1 so an account with no box
 * online still mints one `pending` pass, the stall report's "no box on this account" row; the
 * rest wait in the queue, costing nothing.
 */
export function repoSetupReleaseCount(input: {
  /** Setup-origin tasks of the account that have not ended. */
  liveSetupTasks: number;
  /** Sum of `max_slots` over the account's online boxes. */
  onlineSlots: number;
}): number {
  const window = Math.max(1, Math.floor(input.onlineSlots * REPO_SETUP_MAX_SLOT_SHARE));
  return Math.max(0, Math.min(REPO_SETUP_RELEASE_PER_MINUTE, window - input.liveSetupTasks));
}

/** A whole-number wait, for a sentence: 0 for an empty queue, else at least 1 minute. */
export function repoSetupEtaMinutes(queued: number): number {
  return Math.ceil(queued / REPO_SETUP_RELEASE_PER_MINUTE);
}

/** Why a bulk enable skips one repo. Beside the tier rule's own codes, which the caller adds. */
export const RepoBulkRefusal = {
  /** GitHub refuses every write to an archived repo, so its setup pass could only fail. */
  Archived: 'archived',
  /**
   * A forge with no enable action: GitHub's starts its setup pass, GitLab's its policy re-read
   * (#7928), and any other forge has neither. The stored code predates GitLab's admission.
   */
  NotGithub: 'not_github',
} as const;
export type RepoBulkRefusal = (typeof RepoBulkRefusal)[keyof typeof RepoBulkRefusal];

/** The facts of one repo a bulk verdict reads. */
export type RepoBulkFacts = {
  readonly enabled: boolean;
  readonly archived: boolean;
  readonly private: boolean;
  readonly forge: Forge;
};

/** The forges a bulk enable admits: each has a first work the setup drain releases (#7928). */
const ENABLEABLE_FORGES: ReadonlySet<Forge> = new Set([GITHUB_FORGE, GITLAB_FORGE]);

/**
 * One repo's answer inside a bulk act, gated per repo. `tierRefusal` is the billing rule's
 * code for a PRIVATE repo on this account, or `null` when it may: the rule is
 * `canEnableRepo`'s (`@developerz/billing`, which this package may not import), and tier is an
 * account fact read once, so the caller resolves it and this only says when it applies.
 *
 * Only a transition is gated (`apps/api/docs/repos-and-projects.md`): a repo already on is
 * `unchanged` whatever the tier says now, and turning a repo off is never a billing question.
 */
export function repoBulkVerdict<R extends string>(
  action: RepoBulkAction,
  repo: RepoBulkFacts,
  tierRefusal: R | null,
): { kind: 'apply' } | { kind: 'unchanged' } | { kind: 'refused'; reason: RepoBulkRefusal | R } {
  if (action === RepoBulkAction.Disable) {
    return repo.enabled ? { kind: 'apply' } : { kind: 'unchanged' };
  }
  if (repo.enabled) return { kind: 'unchanged' };
  if (!ENABLEABLE_FORGES.has(repo.forge)) {
    return { kind: 'refused', reason: RepoBulkRefusal.NotGithub };
  }
  if (repo.archived) return { kind: 'refused', reason: RepoBulkRefusal.Archived };
  if (repo.private && tierRefusal !== null) return { kind: 'refused', reason: tierRefusal };
  return { kind: 'apply' };
}

/**
 * A bulk disable's onboarding-PR retires, per INSTALLATION, on one schedule whatever made them
 * (`cron.repo_setup_drain`). A retire with an open PR posts a comment and a close, 2
 * content-creating writes, and GitHub's secondary limit is 80 such writes a minute: 30 retires a
 * minute is 60 writes, leaving the installation's other work room.
 */
export const REPO_RETIRE_RELEASE_PER_MINUTE = 30;

/** The space between them inside a minute, so a tick's retires are a pace rather than a burst. */
export const REPO_RETIRE_SPACING_MS = 60_000 / REPO_RETIRE_RELEASE_PER_MINUTE;

/** The space between one account's bulk setups, which makes the stated rate a schedule. */
export const REPO_SETUP_SPACING_MS = 60_000 / REPO_SETUP_RELEASE_PER_MINUTE;

/** How far ahead a schedule books: one minute's worth, so the rate holds in any minute. */
export const REPO_RELEASE_HORIZON_MS = 60_000;
